import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@shared/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import HeadLayout from "@shared/layouts/HeadLayout";
import useTitle from "@shared/hooks/useTitle";
import { DatePickerWithRange } from "@shared/components/date-picker-with-range";
import { startOfDay, endOfDay, subDays, subMonths } from "date-fns";
import {
  Download,
  Package,
  DollarSign,
  Users,
  Clock,
  Activity,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import { toast } from "sonner";
import { ADMIN_QUERY_KEYS } from "../constants";
import { adminAnalyticsService } from "../services/analytics-service";

export default function AdminAnalyticsPage() {
  const { t } = useTranslation();
  useTitle(t("admin.analytics.title"));

  const [dateRange, setDateRange] = useState({
    from: startOfDay(subMonths(new Date(), 1)),
    to: endOfDay(new Date()),
  });

  const [reportType, setReportType] = useState("overview");
  const [exportFormat, setExportFormat] = useState("pdf");

  // Fetch analytics data
  const { data, isLoading } = useQuery({
    queryKey: [ADMIN_QUERY_KEYS.REPORTS, reportType, dateRange],
    queryFn: () =>
      adminAnalyticsService.getAnalytics({
        from: dateRange.from,
        to: dateRange.to,
        reportType,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const handleExport = async () => {
    try {
      toast.info(t("admin.analytics.exportingReport"));
      await adminAnalyticsService.exportReport({
        from: dateRange.from,
        to: dateRange.to,
        reportType,
        format: exportFormat,
      });
      toast.success(t("admin.analytics.reportExported"));
    } catch {
      toast.error(t("admin.analytics.exportFailed"));
    }
  };

  const handleDateChange = (range) => {
    if (range?.from && range?.to) {
      setDateRange({
        from: startOfDay(range.from),
        to: endOfDay(range.to),
      });
    }
  };

  // Calculate percentage change
  const getPercentageChange = (current, previous) => {
    if (!previous) return 0;
    return (((current - previous) / previous) * 100).toFixed(1);
  };

  const analytics = data?.data || {};

  return (
    <div className="space-y-6">
      <HeadLayout title={t("admin.analytics.title")}>
        <div className="flex gap-2">
          <Select value={exportFormat} onValueChange={setExportFormat}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pdf">PDF</SelectItem>
              <SelectItem value="excel">Excel</SelectItem>
              <SelectItem value="csv">CSV</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleExport} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            {t("admin.analytics.export")}
          </Button>
        </div>
      </HeadLayout>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium mb-2 block">
                {t("admin.analytics.reportType")}
              </label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="overview">
                    {t("admin.analytics.overview")}
                  </SelectItem>
                  <SelectItem value="packages">
                    {t("admin.analytics.packages")}
                  </SelectItem>
                  <SelectItem value="revenue">
                    {t("admin.analytics.revenue")}
                  </SelectItem>
                  <SelectItem value="users">
                    {t("admin.analytics.users")}
                  </SelectItem>
                  <SelectItem value="performance">
                    {t("admin.analytics.performance")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium mb-2 block">
                {t("admin.analytics.dateRange")}
              </label>
              <DatePickerWithRange
                initialDate={dateRange}
                onSelectDate={handleDateChange}
              />
            </div>

            <div className="flex gap-2 items-end">
              <Button
                variant="outline"
                onClick={() =>
                  setDateRange({
                    from: startOfDay(subDays(new Date(), 7)),
                    to: endOfDay(new Date()),
                  })
                }
              >
                {t("admin.analytics.last7Days")}
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  setDateRange({
                    from: startOfDay(subDays(new Date(), 30)),
                    to: endOfDay(new Date()),
                  })
                }
              >
                {t("admin.analytics.last30Days")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <ThreeDotLoader />
        </div>
      ) : (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title={t("admin.analytics.totalRevenue")}
              value={`${analytics.totalRevenue || 0} MAD`}
              change={getPercentageChange(
                analytics.totalRevenue,
                analytics.previousRevenue
              )}
              icon={DollarSign}
              trend={analytics.totalRevenue >= analytics.previousRevenue}
            />
            <MetricCard
              title={t("admin.analytics.totalPackages")}
              value={analytics.totalPackages || 0}
              change={getPercentageChange(
                analytics.totalPackages,
                analytics.previousPackages
              )}
              icon={Package}
              trend={analytics.totalPackages >= analytics.previousPackages}
            />
            <MetricCard
              title={t("admin.analytics.activeUsers")}
              value={analytics.activeUsers || 0}
              change={getPercentageChange(
                analytics.activeUsers,
                analytics.previousActiveUsers
              )}
              icon={Users}
              trend={analytics.activeUsers >= analytics.previousActiveUsers}
            />
            <MetricCard
              title={t("admin.analytics.avgDeliveryTime")}
              value={`${analytics.avgDeliveryTime || 0}h`}
              change={getPercentageChange(
                analytics.avgDeliveryTime,
                analytics.previousDeliveryTime
              )}
              icon={Clock}
              trend={
                analytics.avgDeliveryTime <= analytics.previousDeliveryTime
              }
              invertTrend
            />
          </div>

          {/* Detailed Analytics Tabs */}
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList>
              <TabsTrigger value="overview">
                <Activity className="h-4 w-4 mr-2" />
                {t("admin.analytics.overview")}
              </TabsTrigger>
              <TabsTrigger value="charts">
                <BarChart3 className="h-4 w-4 mr-2" />
                {t("admin.analytics.charts")}
              </TabsTrigger>
              <TabsTrigger value="breakdown">
                <PieChart className="h-4 w-4 mr-2" />
                {t("admin.analytics.breakdown")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Package Status Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle>{t("admin.analytics.packageStatus")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {analytics.packageStatusBreakdown?.map((status) => (
                        <div
                          key={status.status}
                          className="flex items-center justify-between"
                        >
                          <span className="text-sm capitalize">
                            {t(`packages.status.${status.status}`)}
                          </span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 bg-muted rounded-full h-2">
                              <div
                                className="bg-primary h-2 rounded-full"
                                style={{
                                  width: `${
                                    (status.count / analytics.totalPackages) *
                                    100
                                  }%`,
                                }}
                              />
                            </div>
                            <Badge variant="outline">{status.count}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Revenue Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {t("admin.analytics.revenueBreakdown")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <RevenueItem
                        label={t("admin.analytics.deliveryFees")}
                        amount={analytics.deliveryRevenue || 0}
                        total={analytics.totalRevenue}
                      />
                      <RevenueItem
                        label={t("admin.analytics.returnFees")}
                        amount={analytics.returnRevenue || 0}
                        total={analytics.totalRevenue}
                      />
                      <RevenueItem
                        label={t("admin.analytics.otherFees")}
                        amount={analytics.otherRevenue || 0}
                        total={analytics.totalRevenue}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Top Performing Cities */}
                <Card>
                  <CardHeader>
                    <CardTitle>{t("admin.analytics.topCities")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {analytics.topCities?.slice(0, 5).map((city, index) => (
                        <div
                          key={city.city}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{index + 1}</Badge>
                            <span className="text-sm font-medium">
                              {city.city}
                            </span>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {city.count} packages
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Top Clients */}
                <Card>
                  <CardHeader>
                    <CardTitle>{t("admin.analytics.topClients")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {analytics.topClients
                        ?.slice(0, 5)
                        .map((client, index) => (
                          <div
                            key={client._id}
                            className="flex items-center justify-between"
                          >
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary">{index + 1}</Badge>
                              <span className="text-sm font-medium">
                                {client.brandName}
                              </span>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {client.packageCount} packages
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="charts">
              <Card>
                <CardHeader>
                  <CardTitle>{t("admin.analytics.trendsOverTime")}</CardTitle>
                  <CardDescription>
                    {t("admin.analytics.chartsComingSoon")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    {t("admin.analytics.chartsPlaceholder")}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="breakdown">
              <Card>
                <CardHeader>
                  <CardTitle>
                    {t("admin.analytics.detailedBreakdown")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <InfoCard
                        title={t("admin.analytics.totalInvoices")}
                        value={analytics.totalInvoices || 0}
                        subtitle={`${analytics.paidInvoices || 0} ${t(
                          "admin.analytics.paid"
                        )}`}
                      />
                      <InfoCard
                        title={t("admin.analytics.totalPickups")}
                        value={analytics.totalPickups || 0}
                        subtitle={`${analytics.completedPickups || 0} ${t(
                          "admin.analytics.completed"
                        )}`}
                      />
                      <InfoCard
                        title={t("admin.analytics.returnVouchers")}
                        value={analytics.totalReturnVouchers || 0}
                        subtitle={`${analytics.processedReturns || 0} ${t(
                          "admin.analytics.processed"
                        )}`}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}

// Helper Components
function MetricCard({
  title,
  value,
  change,
  icon: Icon,
  trend,
  invertTrend = false,
}) {
  const isPositive = invertTrend ? !trend : trend;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-2">{value}</p>
            {change !== 0 && (
              <div className="flex items-center gap-1 mt-2">
                {isPositive ? (
                  <ArrowUpRight className="h-4 w-4 text-green-500" />
                ) : (
                  <ArrowDownRight className="h-4 w-4 text-red-500" />
                )}
                <span
                  className={`text-sm font-medium ${
                    isPositive ? "text-green-500" : "text-red-500"
                  }`}
                >
                  {Math.abs(change)}%
                </span>
              </div>
            )}
          </div>
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon className="h-6 w-6 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RevenueItem({ label, amount, total }) {
  const percentage = total ? ((amount / total) * 100).toFixed(1) : 0;

  return (
    <div className="flex items-center justify-between">
      <span className="text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <div className="w-24 bg-muted rounded-full h-2">
          <div
            className="bg-primary h-2 rounded-full"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-sm font-medium w-20 text-right">
          {amount} MAD
        </span>
      </div>
    </div>
  );
}

function InfoCard({ title, value, subtitle }) {
  return (
    <div className="border rounded-lg p-4">
      <p className="text-sm text-muted-foreground">{title}</p>
      <p className="text-3xl font-bold mt-2">{value}</p>
      {subtitle && (
        <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
      )}
    </div>
  );
}
